import React, { useMemo } from 'react';
import { Text, View, Image } from '@react-pdf/renderer';
import { pdfSharedStyles } from '../../../pdfSharedStyles';
import { TFunction } from 'i18next';
import { unit as unitEnum } from '../../../../../enum/project';
import { convertFieldValue } from '../../../../../services/UnitConversionService';
import { logDebug, LogTag } from '../../../../../utils/utils';
import { isEmptyUnit } from '../../shared/pdfUnitMerge';
import { parseHtmlToPdfText } from '../../shared/pdfHtmlText';
import * as symbols from '../../../../../enum/symbols';

interface UnpavedOutput {
    unstabilisedRoadThickness?: number;
    geotextileStabilisedThickness?: number;
    geogridStabilisedThickness?: number;
    thicknessSavingGeotextile?: number;
    thicknessSavingGeogrid?: number;
    [key: string]: unknown;
}

interface MethodData {
    data?: Record<string, unknown>;
    load?: Record<string, unknown>;
    subgrade?: Record<string, unknown>;
    traffic?: Record<string, unknown>;
    aggregate?: Record<string, unknown>;
    geoTextile?: {
        selectedGeotextile?: string;
    };
    [key: string]: unknown;
}

interface UnpavedProjectData {
    unit?: string;
    method?: string;
    projectInfo?: {
        data?: {
            method?: string;
            giroud?: MethodData;
            leng?: MethodData;
            [key: string]: unknown;
        };
    };
}

interface UnpavedResultsCoreProps {
    output: UnpavedOutput;
    projectData: UnpavedProjectData;
    projectInfo: any;
    tireImageUnreinforced?: string;
    tireImageReinforced?: string;
    tireImageGeotextile?: string;
    chartImage?: string;
    t: TFunction;
    projectInfoDescription?: string;
    showInputData?: boolean;
    showChart?: boolean;
    showResults?: boolean;
}

interface PdfRowProps {
    label: string;
    symbol?: string | React.ReactElement;
    value?: string;
    unit?: string;
    colSpan?: number;
    condition?: boolean;
    isEven?: boolean;
}

interface PdfTableRow {
    label: string;
    symbol: string | React.ReactElement;
    value: string;
    unit: string;
    colSpan?: number;
}

const PdfRow: React.FC<PdfRowProps> = ({ label, symbol = '', value = '', unit = '', colSpan = 1, condition = true, isEven = false }: PdfRowProps) => {
    if (condition === false) return null;

    // PM UX 2026-04-19: auto-merge Value+Unit when unit is absent (dash/empty/undefined)
    const effectiveColSpan = colSpan === 1 && typeof unit === 'string' && isEmptyUnit(unit) ? 2 : colSpan;

    const rowStyle = [pdfSharedStyles.tableRow, isEven ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow];

    let valueWidth = '20%';
    let showSymbol = true;
    let showUnit = true;

    if (effectiveColSpan === 3) {
        valueWidth = '65%'; // 25 (Symbol) + 20 (Value) + 20 (Unit)
        showSymbol = false;
        showUnit = false;
    } else if (effectiveColSpan === 2) {
        valueWidth = '40%'; // 20 (Value) + 20 (Unit)
        showUnit = false;
    }

    return (
        <View style={rowStyle}>
            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{label}</Text>
            </View>

            {showSymbol && (
                <View style={pdfSharedStyles.tableColSymbol}>
                    <Text style={pdfSharedStyles.tableCell}>{symbol}</Text>
                </View>
            )}

            <View style={[pdfSharedStyles.tableColValue, { width: valueWidth }]}>
                <Text style={pdfSharedStyles.tableCell}>{value}</Text>
            </View>

            {showUnit && (
                <View style={pdfSharedStyles.tableColUnit}>
                    <Text style={pdfSharedStyles.tableCell}>{unit}</Text>
                </View>
            )}
        </View>
    );
};

interface PdfTableProps {
    title: string;
    children: React.ReactNode;
    t: TFunction;
}

const PdfTable: React.FC<PdfTableProps> = ({ title, children, t }: PdfTableProps) => {
    const validChildren = React.Children.toArray(children).filter((child: React.ReactNode): child is React.ReactElement => React.isValidElement(child));
    if (validChildren.length === 0) return null;

    return (
        <View style={[pdfSharedStyles.section, { width: '100%' }]}>
            <View style={pdfSharedStyles.spacer1} />
            <Text style={pdfSharedStyles.tableTitle}>{title}</Text>
            <View style={pdfSharedStyles.spacer0} />
            <View style={pdfSharedStyles.table}>
                <View style={pdfSharedStyles.tableRow}>
                    <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                        <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                    </View>
                    <View style={pdfSharedStyles.tableColHeaderSymbol}>
                        <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                    </View>
                    <View style={pdfSharedStyles.tableColHeaderValue}>
                        <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                    </View>
                    <View style={pdfSharedStyles.tableColHeaderUnit}>
                        <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                    </View>
                </View>
                {validChildren.map((child: React.ReactElement, idx: number) => {
                    if (React.isValidElement(child)) {
                        return React.cloneElement(child as React.ReactElement<PdfRowProps>, { isEven: idx % 2 === 0 });
                    }
                    return child;
                })}
            </View>
        </View>
    );
};

export const UnpavedResultsCore: React.FC<UnpavedResultsCoreProps> = ({
    output,
    projectData,
    projectInfo,
    tireImageUnreinforced,
    tireImageReinforced,
    tireImageGeotextile,
    chartImage,
    t,
    projectInfoDescription,
    showInputData = true,
    showChart = true,
    showResults = true,
}) => {
    const resultsData = output;
    const unitSystem = projectData?.unit || 'Metric';
    // const projectType = 'unpaved'; // Unused variable removed
    const projectMethod = projectData?.method;
    const projectInputs = projectInfo;
    const defaultProjectInfoDescription =
        t(projectMethod === 'leng' ? 'unpaved_leng_project_info_desc_pdf' : 'unpaved_giroud_project_info_desc_pdf') || 'Project Information';
    const resolvedProjectInfoDescription = projectInfoDescription ?? defaultProjectInfoDescription;

    // Check if geotextile is selected - ALIGNED WITH BUTTONPANEL LOGIC
    const hasGeotextile = useMemo(() => {
        // Use the same data path as ButtonPanel for consistency
        const unpavedData = projectInfo as Record<string, unknown> | undefined;

        // UNPAVED_LENG_GEOTEXTILE: Debug received data in PDF component
        logDebug(
            'UnpavedResultsCore',
            'UNPAVED_LENG_GEOTEXTILE: Checking geotextile selection',
            {
                projectInfo_received: projectInfo,
                unpavedData: unpavedData,
                projectInfoExists: !!projectInfo,
                unpavedDataExists: !!unpavedData,
            },
            LogTag.UNPAVED_LENG_GEOTEXTILE
        );

        if (!unpavedData) {
            logDebug('UnpavedResultsCore', 'UNPAVED_LENG_GEOTEXTILE: No unpaved data found', {}, LogTag.UNPAVED_LENG_GEOTEXTILE);
            return false;
        }

        // FLAT structure: geoTextile is directly on unpavedData (projectInfo.data)
        const geoTextileData = unpavedData.geoTextile as Record<string, unknown>;
        const selectedGeotextile = geoTextileData?.selectedGeotextile;

        // UNPAVED_LENG_GEOTEXTILE: Debug method-specific data
        logDebug(
            'UnpavedResultsCore',
            'UNPAVED_LENG_GEOTEXTILE: Method data analysis',
            {
                projectDataMethod: projectData?.method, // Top-level method (primary source)
                projectInfoDataMethod: unpavedData.method, // Method from projectInfo.data (fallback)
                geoTextileObject: geoTextileData,
                selectedGeotextile: selectedGeotextile,
                selectedGeotextileType: typeof selectedGeotextile,
                selectedGeotextileTrimmed: typeof selectedGeotextile === 'string' ? selectedGeotextile.trim() : null,
            },
            LogTag.UNPAVED_LENG_GEOTEXTILE
        );

        const result = !!(selectedGeotextile && typeof selectedGeotextile === 'string' && selectedGeotextile.trim() !== '');

        // UNPAVED_LENG_GEOTEXTILE: Log final result
        logDebug(
            'UnpavedResultsCore',
            'UNPAVED_LENG_GEOTEXTILE: Final hasGeotextile calculation',
            {
                result,
                tireImageGeotextile_exists: !!tireImageGeotextile,
                tireImageGeotextile_value: tireImageGeotextile,
            },
            LogTag.UNPAVED_LENG_GEOTEXTILE
        );

        // Use exact same logic as ButtonPanel
        return result;
    }, [projectInfo, tireImageGeotextile]);

    // Helper function to get unit based on system
    const getUnit = React.useCallback(
        (metricUnit: string, imperialUnit: string) => {
            return unitSystem === 'Metric' ? metricUnit : imperialUnit;
        },
        [unitSystem]
    );

    // Helper for consistent conversion and rounding for input tables
    const convertInputValue = (value: unknown, fieldName: string, metricUnit: string, imperialUnit: string): string => {
        let num: number | undefined;
        if (typeof value === 'number') {
            num = value;
        } else if (typeof value === 'object' && value !== null && 'value' in value) {
            const v = (value as Record<string, unknown>).value;
            if (typeof v === 'number') num = v;
        }
        if (typeof num !== 'number') return 'N/A';
        if (unitSystem === unitEnum.Imperial) {
            const converted = convertFieldValue({ name: fieldName, type: 'number', unit: [metricUnit, imperialUnit], isConvertible: true }, num, unitEnum.SI, unitEnum.Imperial, `pdf.${fieldName}`);
            return converted !== null ? converted.toFixed(3) : num.toString();
        }
        return num.toString();
    };

    // === SEPARATE INPUT TABLES FOR EACH FORM GROUP (following Railway pattern) ===

    // 2. Prepare Load Data table
    const loadDataInputs = useMemo(() => {
        const inputs: PdfTableRow[] = [];
        // FLAT structure: load data is directly on projectInputs
        const loadParams = (projectInputs as any)?.load || {};

        if (projectInputs) {
            if (projectMethod === 'giroud' && typeof loadParams.wheelLoad === 'number') {
                inputs.push({
                    label: t('wheel_load', 'Wheel Load') as string,
                    symbol: symbols.sym_P,
                    value: convertInputValue(loadParams.wheelLoad, 'wheelLoad', 'kN', 'lbf'),
                    unit: getUnit('kN', 'lbf'),
                });
            } else if (projectMethod === 'leng' && typeof loadParams.designAxleLoad === 'number') {
                inputs.push({
                    label: t('design_axle_load', 'Design Axle Load') as string,
                    symbol: symbols.sym_W,
                    value: convertInputValue(loadParams.designAxleLoad, 'designAxleLoad', 'kN', 'lbf'),
                    unit: getUnit('kN', 'lbf'),
                });
            }

            if (typeof loadParams.tyreInflationPressure === 'number') {
                inputs.push({
                    label: t('tyre_inflation_pressure', 'Tyre Inflation Pressure') as string,
                    symbol: symbols.sym_p,
                    value: convertInputValue(loadParams.tyreInflationPressure, 'tyreInflationPressure', 'kPa', 'psi'),
                    unit: getUnit('kPa', 'psi'),
                });
            }
            if (typeof loadParams.esal === 'number') {
                inputs.push({
                    label: t('equivalent_standard_axle_load', 'ESAL') as string,
                    symbol: symbols.sym_pa,
                    value: convertInputValue(loadParams.esal, 'esal', 'kN', 'lbf'),
                    unit: getUnit('kN', 'lbf'),
                });
            }
            if (typeof loadParams.numPassages === 'number') {
                inputs.push({
                    label: t('number_of_passages', 'Number of Passages') as string,
                    symbol: symbols.sym_Np,
                    value: loadParams.numPassages.toString(),
                    unit: '',
                });
            }
            if (typeof loadParams.allowableRutDepth === 'number') {
                inputs.push({
                    label: t('allowable_rut_depth', 'Allowable Rut Depth') as string,
                    symbol: symbols.sym_s,
                    value: unitSystem === unitEnum.Imperial
                        ? (loadParams.allowableRutDepth * 0.0393701).toFixed(2)
                        : loadParams.allowableRutDepth.toString(),
                    unit: getUnit('mm', 'in'),
                });
            }
        }

        return inputs;
    }, [projectMethod, projectInputs, t, getUnit, convertInputValue]);

    // 3. Prepare Subgrade Properties table - only show the selected method based on subgradeInputMethod toggle
    const subgradePropertiesInputs = useMemo(() => {
        const inputs: PdfTableRow[] = [];
        // FLAT structure: subgrade data is directly on projectInputs
        const subgradeParams = (projectInputs as any)?.subgrade || {};

        if (projectInputs && subgradeParams) {
            const inputMethod = subgradeParams.subgradeInputMethod;

            if (inputMethod === 'cbr') {
                if (typeof subgradeParams.cbrBaseCourse === 'number') {
                    inputs.push({
                        label: t('cbr_base_course', 'CBR Base Course') as string,
                        symbol: symbols.sym_cbrbc,
                        value: convertInputValue(subgradeParams.cbrBaseCourse, 'cbrBaseCourse', '%', '%'),
                        unit: '%',
                    });
                }
                if (typeof subgradeParams.cbrSubgrade === 'number') {
                    inputs.push({
                        label: t('cbr_subgrade', 'CBR Subgrade') as string,
                        symbol: symbols.sym_cbrsg,
                        value: convertInputValue(subgradeParams.cbrSubgrade, 'cbrSubgrade', '%', '%'),
                        unit: '%',
                    });
                }
            } else if (inputMethod === 'modulus') {
                if (typeof subgradeParams.ev2BaseCourse === 'number') {
                    inputs.push({
                        label: t('ev2_base_course', 'Ev2 Base Course') as string,
                        symbol: symbols.sym_ev2bc,
                        value: convertInputValue(subgradeParams.ev2BaseCourse, 'ev2BaseCourse', 'MPa', 'psi'),
                        unit: getUnit('MPa', 'psi'),
                    });
                }
                if (typeof subgradeParams.ev2Subgrade === 'number') {
                    inputs.push({
                        label: t('ev2_subgrade', 'Ev2 Subgrade') as string,
                        symbol: symbols.sym_ev2sg,
                        value: convertInputValue(subgradeParams.ev2Subgrade, 'ev2Subgrade', 'MPa', 'psi'),
                        unit: getUnit('MPa', 'psi'),
                    });
                }
            }
        }

        return inputs;
    }, [projectMethod, projectInputs, t, getUnit, convertInputValue]);

    // 4. Prepare Geogrid Selection table (only if selected)
    const geogridSelectionInputs = useMemo(() => {
        const inputs: PdfTableRow[] = [];
        // FLAT structure: geoGrid data is directly on projectInputs
        const geoGridData = (projectInputs as any)?.geoGrid;

        if (geoGridData?.selectedGeogrid && geoGridData.selectedGeogrid.trim() !== '') {
            // Colspan: la cella Value occupa 3 colonne, Symbol e Unit nascosti
            inputs.push({
                label: t('selected_geogrid_product', 'Selected Geogrid Product') as string,
                symbol: '-',
                value: geoGridData.selectedGeogrid,
                unit: '-',
                colSpan: 3,
            });
        }

        return inputs;
    }, [projectInputs, t]);

    // 5. Prepare Geotextile Selection table (only if selected)
    const geotextileSelectionInputs = useMemo(() => {
        const inputs: PdfTableRow[] = [];
        // FLAT structure: geoTextile data is directly on projectInputs
        const geoTextileData = (projectInputs as any)?.geoTextile;

        if (geoTextileData?.selectedGeotextile && geoTextileData.selectedGeotextile.trim() !== '') {
            // Colspan: la cella Value occupa 3 colonne, Symbol e Unit nascosti
            inputs.push({
                label: t('selected_geotextile_product', 'Selected Geotextile Product') as string,
                symbol: '-',
                value: geoTextileData.selectedGeotextile,
                unit: '-',
                colSpan: 3,
            });
        }

        return inputs;
    }, [projectInputs, t]);

    // Process unpaved results data for display
    const unpavedResults = useMemo(() => {
        if (!resultsData) return [];

        const rows: PdfTableRow[] = [];
        rows.push({
            label: t('unstabilised_road_thickness', 'Unstabilised Road Thickness') as string,
            symbol: symbols.sym_hu,
            value: resultsData.unstabilisedRoadThickness != null
                ? (unitSystem === unitEnum.Imperial
                    ? (resultsData.unstabilisedRoadThickness * 3.28084).toFixed(2)
                    : resultsData.unstabilisedRoadThickness.toFixed(2))
                : 'N/A',
            unit: getUnit('m', 'ft'),
        });
        if (hasGeotextile) {
            rows.push({
                label: t('geotextile_stabilised_thickness', 'Geotextile Stabilised Thickness') as string,
                symbol: symbols.sym_hgtx,
                value: resultsData.geotextileStabilisedThickness != null
                    ? (unitSystem === unitEnum.Imperial
                        ? (resultsData.geotextileStabilisedThickness * 3.28084).toFixed(2)
                        : resultsData.geotextileStabilisedThickness.toFixed(2))
                    : 'N/A',
                unit: getUnit('m', 'ft'),
            });
        }
        rows.push({
            label: t('geogrid_stabilised_thickness', 'Geogrid Stabilised Thickness') as string,
            symbol: symbols.sym_hgg,
            value: resultsData.geogridStabilisedThickness != null
                ? (unitSystem === unitEnum.Imperial
                    ? (resultsData.geogridStabilisedThickness * 3.28084).toFixed(2)
                    : resultsData.geogridStabilisedThickness.toFixed(2))
                : 'N/A',
            unit: getUnit('m', 'ft'),
        });
        if (hasGeotextile) {
            rows.push({
                label: t('thickness_saving_geotextile', 'Thickness Saving Geotextile') as string,
                symbol: symbols.sym_delta_h_gtx,
                value: resultsData.thicknessSavingGeotextile?.toFixed(0) || 'N/A',
                unit: '%',
            });
        }
        rows.push({
            label: t('thickness_saving_geogrid', 'Thickness Saving Geogrid') as string,
            symbol: symbols.sym_delta_h_gg,
            value: resultsData.thicknessSavingGeogrid?.toFixed(0) || 'N/A',
            unit: '%',
        });
        return rows;
    }, [resultsData, t, getUnit, hasGeotextile]);

    return (
        <>
            {/* MACREAD Unpaved Description */}
            <View style={pdfSharedStyles.spacer1} />
            <Text style={[pdfSharedStyles.projectInfoProjectType]}>GROUNDMAC - Road Unpaved</Text>
            <View style={pdfSharedStyles.spacer0} />
            <Text>
                {parseHtmlToPdfText(resolvedProjectInfoDescription, pdfSharedStyles.projectInfoSubtitle)}
            </Text>

            {/* Input Data Summary Section - Multiple separate tables */}
            {showInputData && (
                <>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={[pdfSharedStyles.title, { marginTop: 8, marginBottom: 0, textAlign: 'left' }]}>{t('input_data_summary')}</Text>

                    {/* Row 1: Load Data & Subgrade Properties */}
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }}>
                        <View style={{ width: '48%' }}>
                            <PdfTable title={t('load_data', 'Load Data')} t={t}>
                                {loadDataInputs.map((row, idx) => (
                                    <PdfRow key={idx} label={row.label} symbol={row.symbol} value={row.value} unit={row.unit} colSpan={row.colSpan} />
                                ))}
                            </PdfTable>
                        </View>
                        <View style={{ width: '48%' }}>
                            <PdfTable title={t('subgrade_properties', 'Subgrade Properties')} t={t}>
                                {subgradePropertiesInputs.map((row, idx) => (
                                    <PdfRow key={idx} label={row.label} symbol={row.symbol} value={row.value} unit={row.unit} colSpan={row.colSpan} />
                                ))}
                            </PdfTable>
                        </View>
                    </View>

                    {/* Row 2: Geogrid Selection & Geotextile Selection (if exist) */}
                    {(geogridSelectionInputs.length > 0 || geotextileSelectionInputs.length > 0) && (
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }}>
                            {geogridSelectionInputs.length > 0 && (
                                <View style={{ width: '48%' }}>
                                    <PdfTable title={t('geogrid_selection', 'Geogrid Selection')} t={t}>
                                        {geogridSelectionInputs.map((row, idx) => (
                                            <PdfRow
                                                key={idx}
                                                label={row.label}
                                                symbol={row.symbol}
                                                value={row.value}
                                                unit={row.unit}
                                                colSpan={row.colSpan}
                                            />
                                        ))}
                                    </PdfTable>
                                </View>
                            )}
                            {geotextileSelectionInputs.length > 0 && (
                                <View style={{ width: '48%' }}>
                                    <PdfTable title={t('geotextile_selection', 'Geotextile Selection')} t={t}>
                                        {geotextileSelectionInputs.map((row, idx) => (
                                            <PdfRow
                                                key={idx}
                                                label={row.label}
                                                symbol={row.symbol}
                                                value={row.value}
                                                unit={row.unit}
                                                colSpan={row.colSpan}
                                            />
                                        ))}
                                    </PdfTable>
                                </View>
                            )}
                            {/* Spacer if only one table is shown */}
                            {(geogridSelectionInputs.length > 0) !== (geotextileSelectionInputs.length > 0) && (
                                <View style={{ width: '48%' }} />
                            )}
                        </View>
                    )}

                    {/* Chart below all input tables */}
                    {showChart && chartImage && (
                        <View style={pdfSharedStyles.graphicSection} wrap={false}>
                            <View style={pdfSharedStyles.spacer2} />
                            <Text style={pdfSharedStyles.subTitle}>{t('analysis_chart', 'Analysis Chart')}</Text>
                            <Image style={pdfSharedStyles.chartImage} src={chartImage} />
                        </View>
                    )}
                </>
            )}
            {/* todo: dobbiamo capire cosa viene altrim. visualizzato in leng */}
            {/* {projectMethod !== 'giroud' && <View break />} */}
            {/* Results Section */}
            {showResults && (
                <>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={[pdfSharedStyles.title, pdfSharedStyles.resultTitle]}>{t('results') || 'Results'}</Text>
                    <View style={pdfSharedStyles.spacer1} />

                    {/* Tire Images */}
                    {(() => {
                        const renderDecision = hasGeotextile && tireImageGeotextile;

                        // UNPAVED_LENG_GEOTEXTILE: Critical rendering decision log
                        logDebug(
                            'UnpavedResultsCore',
                            'UNPAVED_LENG_GEOTEXTILE: Rendering decision - 2 vs 3 canvases',
                            {
                                hasGeotextile,
                                tireImageGeotextile_exists: !!tireImageGeotextile,
                                tireImageGeotextile_value: tireImageGeotextile,
                                renderDecision,
                                willShow3Canvases: renderDecision,
                                willShow2Canvases: !renderDecision,
                            },
                            LogTag.UNPAVED_LENG_GEOTEXTILE
                        );

                        return renderDecision;
                    })() ? (
                        <View style={pdfSharedStyles.graphicImagesContainer}>
                            <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '40%', height: '150' }}>
                                {tireImageUnreinforced ? (
                                    <Image key={tireImageUnreinforced.slice(0, 32)} style={pdfSharedStyles.graphicImage_x3} src={tireImageUnreinforced} />
                                ) : (
                                    <Text style={pdfSharedStyles.graphicDescription}>Unreinforced image not available</Text>
                                )}
                                <Text style={pdfSharedStyles.graphicDescription}>{t('left_unstabilized')}</Text>
                            </View>
                            <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '30%', height: '150' }}>
                                {tireImageReinforced ? (
                                    <Image key={tireImageReinforced.slice(0, 32)} style={pdfSharedStyles.graphicImage_x3} src={tireImageReinforced} />
                                ) : (
                                    <Text style={pdfSharedStyles.graphicDescription}>Reinforced image not available</Text>
                                )}
                                <Text style={pdfSharedStyles.graphicDescription}>{hasGeotextile ? t('geogrid_stabilized', 'GEOGRID stabilized') : t('right_stabilized', 'Stabilized')}</Text>
                            </View>
                            <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '30%', height: '150' }}>
                                {tireImageGeotextile ? (
                                    <Image key={tireImageGeotextile.slice(0, 32)} style={pdfSharedStyles.graphicImage_x3} src={tireImageGeotextile} />
                                ) : (
                                    <Text style={pdfSharedStyles.graphicDescription}>Geotextile image not available</Text>
                                )}
                                <Text style={pdfSharedStyles.graphicDescription}>{t('geotextile', 'Geotextile')}</Text>
                            </View>
                        </View>
                    ) : (
                        <View style={pdfSharedStyles.graphicImagesContainer}>
                            <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '50%' }}>
                                {tireImageUnreinforced ? (
                                    <Image key={tireImageUnreinforced.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={tireImageUnreinforced} />
                                ) : (
                                    <Text style={pdfSharedStyles.graphicDescription}>Unreinforced image not available</Text>
                                )}
                                <Text style={pdfSharedStyles.graphicDescription}>{t('left_unstabilized')}</Text>
                            </View>
                            <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '50%' }}>
                                {tireImageReinforced ? (
                                    <Image key={tireImageReinforced.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={tireImageReinforced} />
                                ) : (
                                    <Text style={pdfSharedStyles.graphicDescription}>Reinforced image not available</Text>
                                )}
                                <Text style={pdfSharedStyles.graphicDescription}>{hasGeotextile ? t('geogrid_stabilized', 'GEOGRID stabilized') : t('right_stabilized', 'Stabilized')}</Text>
                            </View>
                        </View>
                    )}

                    <View style={pdfSharedStyles.spacer3} />

                    {/* Results Table */}
                    <PdfTable title={t('results_summary', 'Results Summary')} t={t}>
                        {unpavedResults.map((row, index) => (
                            <PdfRow key={index} label={row.label} symbol={row.symbol} value={row.value} unit={row.unit} colSpan={row.colSpan} />
                        ))}
                    </PdfTable>
                </>
            )}
        </>
    );
};
